/**
 * AdminRetentionPage.js — /admin/retention
 *
 * RC 1.47.3 Phase 1 — Admin observability for the daily artefact-
 * retention cron (`ndorflow_artefact_retention`).
 *
 * READ-ONLY tile/table. Backed by the already-shipped
 *   GET /api/admin/cron/artefact-retention-runs?limit=N
 * (capped at 100 server-side). Renders one row per run with the
 * full audit shape persisted in `artefact_retention_runs`:
 *
 *   - run_id
 *   - run_started_at  / completed_at
 *   - dry_run
 *   - exempt_user
 *   - window_days
 *   - rows_tombstoned: { cv_variants, application_documents }
 *   - errors / error_message
 *
 * Status colouring (operator directive):
 *   - GREEN  = success (errors=0 AND not a dry-run)
 *   - YELLOW = dry-run preview
 *   - RED    = errors > 0
 *
 * Out-of-scope for this slice (intentional):
 *   - No manual-run trigger button. The
 *     `POST /api/admin/cron/run-artefact-retention` endpoint stays
 *     curl-only during the 60-day operator soak — keeps the UI
 *     surface read-only and prevents accidental clicks.
 *   - No retention-window editor. Window is operator-controlled via
 *     `pg_cron` schedule (migration 052).
 *   - No alteration of the `tombstone_expired_artefacts` function.
 *   - No DB migrations.
 *
 * Access: same gating as every other admin page —
 * `WorkspaceGuard workspace="admin"` + `require_admin` server-side.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import {
  Loader2, RefreshCw, ShieldAlert, Info, AlertTriangle,
  CheckCircle2, FlaskConical, Clock,
} from 'lucide-react';
import {
  fmtDateTime, durationMs, fmtDuration, classifyRun,
  TONE_CLASSES, TONE_DOT, shortenUuid,
} from './adminRetentionHelpers';

// Re-export pure helpers so callers (and existing imports) keep
// working even if they reach for them from this module path.
export {
  fmtDateTime, durationMs, fmtDuration, classifyRun, shortenUuid,
} from './adminRetentionHelpers';

// ── icon binding (DOM/JSX deps live here, not in the helper) ──────

const STATUS_ICONS = {
  green:  CheckCircle2,
  yellow: FlaskConical,
  red:    AlertTriangle,
};

function classifyRunWithIcon(run) {
  const c = classifyRun(run);
  return { ...c, Icon: STATUS_ICONS[c.tone] || CheckCircle2 };
}

// ── desktop row ──────────────────────────────────────────────────

function DesktopRow({ run }) {
  const { tone, label, Icon } = classifyRunWithIcon(run);
  const cv  = Number(run?.rows_tombstoned?.cv_variants           ?? 0);
  const ad  = Number(run?.rows_tombstoned?.application_documents ?? 0);
  const dur = durationMs(run?.run_started_at, run?.completed_at);
  return (
    <tr
      className="border-b border-ndor-edge last:border-b-0 align-top"
      data-testid={`retention-run-row-${run.id}`}
      data-tone={tone}
    >
      <td className="py-3 pr-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${TONE_DOT[tone]}`} />
          <Badge
            variant="outline"
            className={`${TONE_CLASSES[tone]} text-[10px] uppercase tracking-wide`}
            data-testid={`retention-run-status-${run.id}`}
          >
            <Icon className="h-3 w-3 mr-1" />
            {label}
          </Badge>
        </div>
      </td>
      <td className="py-3 pr-3 font-mono-ui text-[11px] text-ndor-ink-muted whitespace-nowrap">
        <span title={run.id}>{shortenUuid(run.id)}</span>
      </td>
      <td className="py-3 pr-3 text-xs text-ndor-ink whitespace-nowrap">
        {fmtDateTime(run.run_started_at)}
      </td>
      <td className="py-3 pr-3 text-xs text-ndor-ink whitespace-nowrap">
        {fmtDateTime(run.completed_at)}
      </td>
      <td className="py-3 pr-3 text-xs text-ndor-ink-muted whitespace-nowrap">
        {fmtDuration(dur)}
      </td>
      <td className="py-3 pr-3 text-xs text-ndor-ink whitespace-nowrap">
        {Number.isFinite(Number(run.window_days)) ? `${run.window_days}d` : '—'}
      </td>
      <td className="py-3 pr-3 text-xs text-ndor-ink whitespace-nowrap">
        {run.exempt_user ? (
          <span
            className="font-mono-ui text-[11px]"
            title={run.exempt_user}
          >
            {shortenUuid(run.exempt_user)}
          </span>
        ) : (
          <span className="text-ndor-ink-muted">none</span>
        )}
      </td>
      <td className="py-3 pr-3 text-xs text-ndor-ink whitespace-nowrap">
        <span className="font-mono-ui">{cv}</span>
      </td>
      <td className="py-3 pr-3 text-xs text-ndor-ink whitespace-nowrap">
        <span className="font-mono-ui">{ad}</span>
      </td>
      <td className="py-3 pr-0 text-xs text-ndor-ink">
        {Number(run.errors ?? 0) > 0 ? (
          <div className="flex items-start gap-1.5 max-w-md">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-600 shrink-0 mt-0.5" />
            <span
              className="text-rose-200 break-words"
              data-testid={`retention-run-error-${run.id}`}
            >
              {run.error_message || `${run.errors} error(s)`}
            </span>
          </div>
        ) : (
          <span className="text-ndor-ink-muted">—</span>
        )}
      </td>
    </tr>
  );
}

// ── mobile card ──────────────────────────────────────────────────

function MobileCard({ run }) {
  const { tone, label, Icon } = classifyRunWithIcon(run);
  const cv  = Number(run?.rows_tombstoned?.cv_variants           ?? 0);
  const ad  = Number(run?.rows_tombstoned?.application_documents ?? 0);
  const dur = durationMs(run?.run_started_at, run?.completed_at);
  return (
    <Card
      className="border-ndor-edge bg-ndor-surface"
      data-testid={`retention-run-card-${run.id}`}
      data-tone={tone}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${TONE_DOT[tone]}`} />
              <Badge
                variant="outline"
                className={`${TONE_CLASSES[tone]} text-[10px] uppercase tracking-wide`}
              >
                <Icon className="h-3 w-3 mr-1" />
                {label}
              </Badge>
            </div>
            <p
              className="text-[11px] font-mono-ui text-ndor-ink-muted break-all"
              title={run.id}
            >
              {run.id}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Window</p>
            <p className="text-sm font-semibold text-ndor-ink">
              {Number.isFinite(Number(run.window_days)) ? `${run.window_days}d` : '—'}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Started</dt>
            <dd className="text-ndor-ink">{fmtDateTime(run.run_started_at)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Completed</dt>
            <dd className="text-ndor-ink">{fmtDateTime(run.completed_at)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Duration</dt>
            <dd className="text-ndor-ink-muted">{fmtDuration(dur)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Exempt user</dt>
            <dd className="text-ndor-ink font-mono-ui text-[11px] break-all">
              {run.exempt_user ? shortenUuid(run.exempt_user) : 'none'}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">CV variants</dt>
            <dd className="text-ndor-ink font-mono-ui">{cv}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Cover letters</dt>
            <dd className="text-ndor-ink font-mono-ui">{ad}</dd>
          </div>
        </dl>

        {Number(run.errors ?? 0) > 0 ? (
          <div
            className="rounded-md border border-rose-500/40 bg-rose-500/10 p-2.5 text-xs text-rose-200 flex items-start gap-2"
            data-testid={`retention-run-card-error-${run.id}`}
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <span className="break-words">{run.error_message || `${run.errors} error(s)`}</span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── summary tiles ────────────────────────────────────────────────

function SummaryTiles({ runs }) {
  const stats = useMemo(() => {
    const total       = runs.length;
    const errors      = runs.filter((r) => Number(r?.errors ?? 0) > 0).length;
    const dryRuns     = runs.filter((r) => r?.dry_run && Number(r?.errors ?? 0) === 0).length;
    const successes   = runs.filter((r) => !r?.dry_run && Number(r?.errors ?? 0) === 0).length;
    const cvSum       = runs.reduce((acc, r) => acc + Number(r?.rows_tombstoned?.cv_variants           ?? 0), 0);
    const adSum       = runs.reduce((acc, r) => acc + Number(r?.rows_tombstoned?.application_documents ?? 0), 0);
    const lastRun     = runs[0]?.run_started_at || null;
    return { total, errors, dryRuns, successes, cvSum, adSum, lastRun };
  }, [runs]);

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
      data-testid="retention-summary-tiles"
    >
      <Card className="border-ndor-edge bg-ndor-surface">
        <CardContent className="p-3 sm:p-4">
          <p className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Last run</p>
          <p className="text-xs font-medium text-ndor-ink mt-1 truncate" title={stats.lastRun || '—'}>
            {stats.lastRun ? fmtDateTime(stats.lastRun) : '—'}
          </p>
        </CardContent>
      </Card>
      <Card className="border-ndor-edge bg-ndor-surface">
        <CardContent className="p-3 sm:p-4">
          <p className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Total runs</p>
          <p className="text-lg font-semibold text-ndor-ink font-mono-ui">{stats.total}</p>
        </CardContent>
      </Card>
      <Card className="border-emerald-500/40 bg-emerald-500/10/40">
        <CardContent className="p-3 sm:p-4">
          <p className="text-[10px] uppercase tracking-wide text-emerald-200">Successes</p>
          <p className="text-lg font-semibold text-emerald-200 font-mono-ui">{stats.successes}</p>
        </CardContent>
      </Card>
      <Card className="border-amber-500/40 bg-amber-500/10/40">
        <CardContent className="p-3 sm:p-4">
          <p className="text-[10px] uppercase tracking-wide text-amber-200">Dry-runs</p>
          <p className="text-lg font-semibold text-amber-200 font-mono-ui">{stats.dryRuns}</p>
        </CardContent>
      </Card>
      <Card className={stats.errors > 0 ? 'border-rose-500/40 bg-rose-500/10/40' : 'border-ndor-edge bg-ndor-surface'}>
        <CardContent className="p-3 sm:p-4">
          <p className={`text-[10px] uppercase tracking-wide ${stats.errors > 0 ? 'text-rose-200' : 'text-ndor-ink-muted'}`}>
            Errors
          </p>
          <p className={`text-lg font-semibold font-mono-ui ${stats.errors > 0 ? 'text-rose-200' : 'text-ndor-ink'}`}>
            {stats.errors}
          </p>
        </CardContent>
      </Card>
      <Card className="border-ndor-edge bg-ndor-surface">
        <CardContent className="p-3 sm:p-4">
          <p className="text-[10px] uppercase tracking-wide text-ndor-ink-muted">Tombstoned (Σ)</p>
          <p className="text-xs text-ndor-ink mt-1">
            <span className="font-mono-ui font-medium">{stats.cvSum}</span> CV ·{' '}
            <span className="font-mono-ui font-medium">{stats.adSum}</span> letters
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────

const DEFAULT_LIMIT = 30;

export default function AdminRetentionPage() {
  const [runs,      setRuns]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [limit,     setLimit]     = useState(DEFAULT_LIMIT);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(
        `/api/admin/cron/artefact-retention-runs?limit=${encodeURIComponent(limit)}`,
      );
      const next = Array.isArray(data?.runs) ? data.runs : [];
      // Endpoint already returns newest-first; defensive sort here
      // protects the UI if the backend ever changes ordering.
      next.sort((a, b) => {
        const ta = a?.run_started_at ? new Date(a.run_started_at).getTime() : 0;
        const tb = b?.run_started_at ? new Date(b.run_started_at).getTime() : 0;
        return tb - ta;
      });
      setRuns(next);
      setFetchedAt(new Date());
    } catch (e) {
      setError(e?.message || 'Failed to load retention runs.');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { void load(); }, [load]);

  const showSkeleton = loading && runs.length === 0;
  const isEmpty      = !loading && runs.length === 0 && !error;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="admin-retention-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif-display font-semibold text-ndor-ink">
            Artefact Retention
          </h1>
          <p className="mt-1.5 text-sm text-ndor-ink-secondary max-w-2xl">
            Daily audit trail for{' '}
            <code className="px-1 py-0.5 rounded bg-ndor-surface-alt text-xs">
              ndorflow_artefact_retention
            </code>{' '}
            cron — read from{' '}
            <code className="px-1 py-0.5 rounded bg-ndor-surface-alt text-xs">
              GET /api/admin/cron/artefact-retention-runs
            </code>
            . Schedule: <code className="text-xs">0 3 * * *</code> UTC. Operator
            account is exempt for the 60-day soak (review on / after{' '}
            <strong>2026-07-01</strong>).
          </p>
        </div>
        <div className="flex items-center gap-3">
          {fetchedAt ? (
            <span
              className="text-[11px] text-ndor-ink-muted font-mono-ui hidden sm:inline"
              data-testid="retention-fetched-at"
            >
              Fetched: {fetchedAt.toLocaleTimeString()}
            </span>
          ) : null}
          <label className="hidden sm:flex items-center gap-2 text-xs text-ndor-ink-muted">
            <span>Limit</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value) || DEFAULT_LIMIT)}
              className="rounded border border-ndor-edge bg-ndor-surface px-2 py-1 text-xs"
              data-testid="retention-limit-select"
              disabled={loading}
            >
              {[10, 30, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            className="border-ndor-edge"
            data-testid="admin-retention-refresh"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </header>

      {/* Error banner */}
      {error ? (
        <div
          className="rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 p-4 text-sm flex items-start gap-2"
          data-testid="admin-retention-error"
        >
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="break-words">{error}</span>
        </div>
      ) : null}

      {/* Summary tiles */}
      {runs.length > 0 ? <SummaryTiles runs={runs} /> : null}

      {/* Skeleton */}
      {showSkeleton ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="admin-retention-skeleton">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border-ndor-edge bg-ndor-surface animate-pulse h-32" aria-hidden />
          ))}
        </div>
      ) : null}

      {/* Empty */}
      {isEmpty ? (
        <Card className="border-ndor-edge bg-ndor-surface" data-testid="admin-retention-empty">
          <CardContent className="p-6 text-center space-y-2">
            <Clock className="h-8 w-8 text-ndor-ink-muted mx-auto" />
            <p className="text-sm font-medium text-ndor-ink">No retention runs recorded yet.</p>
            <p className="text-xs text-ndor-ink-muted max-w-md mx-auto">
              The first scheduled run will fire at the next 03:00 UTC tick. Until
              then, this page will stay empty unless an operator triggers a manual
              run via{' '}
              <code className="text-[11px] bg-ndor-surface-alt px-1 py-0.5 rounded">
                POST /api/admin/cron/run-artefact-retention
              </code>
              .
            </p>
          </CardContent>
        </Card>
      ) : null}

      {/* Desktop table */}
      {runs.length > 0 ? (
        <Card className="border-ndor-edge bg-ndor-surface hidden lg:block" data-testid="admin-retention-table-card">
          <CardHeader>
            <CardTitle className="text-base font-serif-display font-semibold">Run history</CardTitle>
            <CardDescription className="text-xs">
              Newest first · {runs.length} run{runs.length === 1 ? '' : 's'} loaded · capped at{' '}
              {limit}.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 sm:p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="admin-retention-table">
                <thead>
                  <tr className="border-b border-ndor-edge bg-ndor-surface-alt/40">
                    <th className="py-2 pl-4 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Status</th>
                    <th className="py-2 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Run id</th>
                    <th className="py-2 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Started</th>
                    <th className="py-2 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Completed</th>
                    <th className="py-2 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Duration</th>
                    <th className="py-2 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Window</th>
                    <th className="py-2 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Exempt</th>
                    <th className="py-2 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">CV variants</th>
                    <th className="py-2 pr-3 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Cover letters</th>
                    <th className="py-2 pr-4 text-[10px] uppercase tracking-wide text-ndor-ink-muted">Errors</th>
                  </tr>
                </thead>
                <tbody className="[&>tr>td:first-child]:pl-4 [&>tr>td:last-child]:pr-4">
                  {runs.map((r) => <DesktopRow key={r.id} run={r} />)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Mobile / tablet card list */}
      {runs.length > 0 ? (
        <div className="lg:hidden space-y-3" data-testid="admin-retention-card-list">
          {runs.map((r) => <MobileCard key={r.id} run={r} />)}
        </div>
      ) : null}

      {/* Help footer */}
      <section
        className="rounded-lg border border-ndor-edge bg-ndor-surface-alt/50 p-4 sm:p-5 flex items-start gap-3"
        data-testid="admin-retention-help"
      >
        <Info className="h-4 w-4 text-ndor-info shrink-0 mt-0.5" />
        <div className="text-xs sm:text-sm text-ndor-ink-secondary space-y-1.5">
          <p>
            Status colouring: <span className="text-emerald-200 font-medium">Success</span>{' '}
            (errors=0, non-dry) · <span className="text-amber-200 font-medium">Dry-run</span>{' '}
            (preview, no writes) · <span className="text-rose-200 font-medium">Error</span>{' '}
            (errors {'>'} 0). Manual runs use the same audit table — distinguished
            by the <code className="text-[11px] bg-ndor-surface-alt px-1 py-0.5 rounded">dry_run</code>{' '}
            and <code className="text-[11px] bg-ndor-surface-alt px-1 py-0.5 rounded">exempt_user</code>{' '}
            columns.
          </p>
          <p>
            Manual trigger (curl-only during the soak). RC 1.48.9 P0-5 —{' '}
            <code className="text-[11px] bg-ndor-surface-alt px-1 py-0.5 rounded">dry_run</code>{' '}
            now defaults to <strong>true</strong>; an empty body previews safely.
            Pass <code className="text-[11px] bg-ndor-surface-alt px-1 py-0.5 rounded">{'"dry_run": false'}</code>{' '}
            explicitly to actually tombstone:
          </p>
          <pre className="rounded bg-ndor-surface border border-ndor-edge p-2.5 text-[11px] text-ndor-ink overflow-x-auto font-mono-ui m-0">
{`curl -X POST "$API_URL/api/admin/cron/run-artefact-retention" \\
     -H "Authorization: Bearer $ADMIN_JWT" \\
     -H "Content-Type: application/json" \\
     -d '{"window_days": 30, "dry_run": true,
          "exempt_user": "552c3e57-50a5-427f-9c6b-8419d7d78cb0"}'`}
          </pre>
        </div>
      </section>
    </div>
  );
}
